import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@/lib/theme-context';
import { useThemeSettings } from '@/lib/theme-settings';
import { readSnowPreference, SnowPreference } from '@/lib/snow';

/**
 * Seasonal snowfall.
 *
 * Snow is NOT a third theme anymore — it belongs to dark mode. The admin's
 * `winter` entry in `enabledThemes` is now the master switch ("snowfall allowed
 * on this storefront"): when it is on and the visitor is in dark mode, this
 * mounts the snowfall. Site users additionally get an off switch in their
 * account settings; that preference is read here so the overlay live-updates.
 *
 * The overlay is self-contained and sits below the nav bar and modals.
 */
export function WinterOverlay() {
  const { isDark } = useTheme();
  const { enabledThemes, loading } = useThemeSettings();
  const [mounted, setMounted] = useState(false);

  // Live-track the account-settings snow toggle. Same-tab storage events do
  // not fire, so a tiny notifier in lib/snow broadcasts changes.
  const [snowEnabled, setSnowEnabled] = useState(() => readSnowPreference() !== 'off');
  useEffect(() => {
    return SnowPreference.subscribe((next) => setSnowEnabled(next !== 'off'));
  }, []);

  const shouldShow = useMemo(
    () => !loading && isDark && enabledThemes.includes('winter') && snowEnabled,
    [isDark, enabledThemes, loading, snowEnabled],
  );

  useEffect(() => {
    if (!shouldShow) {
      setMounted(false);
      return;
    }
    // Short delay so switching themes doesn't paint 60 elements over the page
    // mid-transition; reads as weather arriving rather than a flash.
    const id = window.setTimeout(() => setMounted(true), 250);
    return () => window.clearTimeout(id);
  }, [shouldShow]);

  if (!mounted) return null;
  return <WinterSnowfall count={56} />;
}

/**
 * Imported lazily-ish: the flake field is pure CSS, so this stays a tiny local
 * component rather than a shared one — it is the only consumer.
 */
function WinterSnowfall({ count }: { count: number }) {
  const flakes = useMemo(() => buildFlakes(count), [count]);
  return (
    <div
      aria-hidden="true"
      className="snowfall fixed inset-0 z-[100] overflow-hidden pointer-events-none select-none"
    >
      {flakes.map((f, i) => (
        <span
          key={i}
          className="snow-flake absolute -top-[10vh] rounded-full"
          style={
            {
              left: `${f.left}%`,
              width: `${f.size}px`,
              height: `${f.size}px`,
              opacity: f.opacity,
              filter: f.blur ? `blur(${f.blur}px)` : undefined,
              animationDuration: `${f.duration}s`,
              animationDelay: `${f.delay}s`,
              '--snow-drift': `${f.drift}px`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}

interface Flake {
  left: number;
  size: number;
  duration: number;
  delay: number;
  drift: number;
  opacity: number;
  blur: number;
}

/** mulberry32 — small, fast, seedable, so the field doesn't reshuffle per render. */
function rng(seed: number) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function buildFlakes(count: number): Flake[] {
  const rand = rng(0x5eed);
  return Array.from({ length: count }, () => {
    // Bias the size curve so big flakes stay rare.
    const r = rand();
    return {
      left: rand() * 100,
      size: 2 + r * r * 6,
      duration: 7 + rand() * 11,
      delay: -rand() * 18,
      drift: (rand() - 0.5) * 90,
      opacity: 0.55 + rand() * 0.45,
      blur: rand() > 0.82 ? 1.4 : 0,
    };
  });
}